import React from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { useListEdition } from "@planzone/view-models";
import { DateTimeInput } from "@common/dateTimeInput";
import { Props } from "./types";
import styles from "./styles.scss";

function DatesPickers({ list, projectTimezone, isInvalid, readOnly }: Props) {
  const { t } = useTranslation();
  const [startDate, setStartDate] = React.useState(list.startDate);
  const [endDate, setEndDate] = React.useState(list.endDate);
  const { editList } = useListEdition();
  const zone = projectTimezone || DateTime.local().zoneName;
  const userTimezone = DateTime.local().zoneName;
  // Bounds are hour-precise (e.g. an HOUR-granularity constraint), not
  // necessarily local midnight, while DateTimeInput only lets the user pick a
  // day (committed at noon). The picked instant must be clamped to the bound's
  // exact instant when the picked day is the bound's day, otherwise it can land
  // before/after the real bound and be rejected by the backend.
  const boundDateTime = (iso?: string | null) => {
    if (!iso) return undefined;
    const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone(zone);
    return dt.isValid ? dt : undefined;
  };
  const toCalendarDate = (dt?: DateTime) =>
    dt ? { year: dt.year, month: dt.month - 1, day: dt.day } : undefined;
  const clamp = (iso: string, min?: DateTime, max?: DateTime) => {
    const picked = DateTime.fromISO(iso, { zone: "utc" });
    const clamped =
      min && picked < min ? min : max && picked > max ? max : picked;
    return clamped.toUTC().toISO() ?? "";
  };
  const startMinBound = boundDateTime(list.minStartDate);
  const startMaxBound = boundDateTime(
    list.maxStartDate || endDate || list.maxEndDate,
  );
  const endMinBound = boundDateTime(
    list.minEndDate || startDate || list.minStartDate,
  );

  React.useEffect(() => {
    setStartDate(list.startDate);
  }, [list.startDate]);

  React.useEffect(() => {
    setEndDate(list.endDate);
  }, [list.endDate]);

  return (
    <div className={styles.datesWrapper}>
      <div className={styles.group}>
        <h3 className={styles.title}>{t("listRetrieval.startDate")}</h3>
        <DateTimeInput
          dateTime={startDate}
          placeholder={t("listRetrieval.startDate")}
          projectTimezone={zone}
          displayTimezone={userTimezone}
          minDate={toCalendarDate(startMinBound)}
          maxDate={toCalendarDate(startMaxBound)}
          isInvalid={isInvalid}
          readOnly={readOnly}
          clear={() =>
            editList({
              list,
              nextList: { ...list, startDate: "" },
            })
          }
          input={(date) => {
            const nextDate = new Date(
              clamp(date, startMinBound, startMaxBound),
            );

            editList({
              list,
              nextList: {
                ...list,
                startDate:
                  !!list.startDate &&
                  nextDate.toISOString() ===
                    new Date(list.startDate).toISOString()
                    ? ""
                    : nextDate.toISOString(),
              },
            });
          }}
        />
      </div>
      <div className={styles.group}>
        <h3 className={styles.title}>{t("listRetrieval.endDate")}</h3>
        <DateTimeInput
          dateTime={endDate}
          placeholder={t("listRetrieval.endDate")}
          projectTimezone={zone}
          displayTimezone={userTimezone}
          minDate={toCalendarDate(endMinBound)}
          isInvalid={isInvalid}
          readOnly={readOnly}
          clear={() =>
            editList({
              list,
              nextList: { ...list, endDate: "" },
            })
          }
          input={(date) => {
            const nextDate = new Date(clamp(date, endMinBound));

            editList({
              list,
              nextList: {
                ...list,
                endDate:
                  !!list.endDate &&
                  nextDate.toISOString() ===
                    new Date(list.endDate).toISOString()
                    ? ""
                    : nextDate.toISOString(),
              },
            });
          }}
        />
      </div>
    </div>
  );
}

export { DatesPickers };
